/**
 * backgroundGeoBridge.ts — FASE 1.1 (Luglio 2026)
 * FASE 9 (Luglio 2026) — Rimosso @ts-nocheck.
 *
 * BRIDGE UFFICIALE per @capacitor-community/background-geolocation.
 *
 * Questo file è l'UNICO punto di integrazione con il plugin GPS nativo.
 * Utilizza il plugin community (mantenuto, 1000+ stelle GitHub) invece
 * del vecchio plugin custom ForegroundGeolocation (non funzionante).
 *
 * Il plugin community sopravvive a:
 *   - Schermo spento (Android 12+)
 *   - App in background
 *   - Process death (Foreground Service nativo corretto)
 *
 * API ESPOSTA (retrocompatibile con tutti i consumer esistenti):
 *   start(callback?) → boolean
 *   stop()
 *   configure(opts)
 *   isTracking() → boolean
 *   getTrackingStatus() → TrackingStatus
 *   getPendingStats() → PendingStats
 *   syncPendingLocations() → { triggered }
 *   ackPendingFlush(ids) → { acked }
 *   requestBatteryOptimizationExemption()
 *   isAvailable() → boolean
 *   isGpsAvailable() → boolean
 *   setGpsAvailable(v)
 *   addDistanceKm(km)
 *   onFlush(callback) → cleanup function
 */

import { Capacitor } from "@capacitor/core";

// FASE 4 (Luglio 2026): Heartbeat monitoring per rilevare plugin death su Xiaomi/Huawei
import {
  heartbeatRecordFix,
  startHeartbeatMonitor,
  stopHeartbeatMonitor,
} from "./pluginHealthCheck";

// FIX GPS-ADAPTIVE (Set 2026): campionamento adattivo in base alla velocità
import { AdaptiveIntervalController } from "./adaptiveGps";

// ─────────────────────────────────────────────────────────────────────────────
// FIX PLUGIN-BACKGROUND (7 Ott 2026) — CAUSA REALE della "linea retta":
//
// su npm il pacchetto @capacitor-community/background-geolocation pubblica
// SOLO i tipi (definitions.d.ts) e NESSUN entry point JS: il suo package.json
// non è risolvibile dai bundler. Il precedente import() dinamico, in build,
// finiva sullo stub dell'alias Vite (senza `addWatcher`) → l'avvio del watcher
// falliva, il Foreground Service nativo non partiva mai e la registrazione
// restava agganciata alla geolocalizzazione della WebView, che Android
// sospende a schermo spento: nessun fix in background, tracciato interrotto e
// linea retta al rientro (segnalazione utente 7 Ott 2026).
//
// L'istanza del plugin vive in `backgroundGeoPlugin.ts` (registerPlugin):
// quel modulo NON va importato per i tipi da qui — vedi il commento in testa
// al file stesso.
// ─────────────────────────────────────────────────────────────────────────────
import { BackgroundGeolocation } from "./backgroundGeoPlugin";

// ── Tipi ────────────────────────────────────────────────────────────────────

export interface GpsPosition {
  lat: number;
  lng: number;
  accuracy: number;
  /** Velocità in km/h (il fix nativo la espone in m/s). */
  speed: number;
  /** Quota in metri, se il fix la fornisce. */
  altitude?: number | null;
  /** Direzione in gradi, se il fix la fornisce. */
  heading?: number | null;
  timestamp: number;
  totalDistanceKm: number;
  sessionId: string;
}

export interface PendingFlushData {
  locations: string;
  localIds: string;
  count: number;
  batchId: number;
  sessionId: string;
}

export interface TrackingStatus {
  tracking: boolean;
  pendingLocations: number;
  totalDistanceKm: number;
  sessionId: string;
}

export interface PendingStats {
  totalPending: number;
  sessionPending: number;
  sessionId: string;
}

export interface GpsAvailability {
  available: boolean;
  errorCode?: number;
  errorMessage?: string;
}

export interface TrackingConfig {
  intervalHighway?: number;
  intervalFast?: number;
  intervalUrban?: number;
  intervalIdle?: number;
  fastestInterval?: number;
  maxWaitTime?: number;
  distanceFilter?: number;
}

// ── Stato interno ───────────────────────────────────────────────────────────

let _watcherId: string | null = null;
let _isTracking = false;
let _totalDistanceKm = 0;
let _gpsAvailable = false;
let _positionListeners: Array<(pos: GpsPosition) => void> = [];
let _flushListeners: Array<(data: PendingFlushData) => void> = [];
let _sessionId = "";
let _pendingLocations = 0;
let _lastPosition: GpsPosition | null = null;

// FIX GPS-ADAPTIVE (Set 2026): stato per il campionamento adattivo.
let _trackingConfig: TrackingConfig = {};
let _adaptiveController: AdaptiveIntervalController | null = null;
let _currentIntervalMs = 3000;

/** Intervallo attivo corrente del watcher (per status/test). */
export function getCurrentInterval(): number {
  return _currentIntervalMs;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function toGpsPosition(loc: any): GpsPosition {
  return {
    lat: loc.latitude ?? 0,
    lng: loc.longitude ?? 0,
    accuracy: loc.accuracy ?? 0,
    speed: (loc.speed ?? 0) * 3.6, // m/s → km/h
    // FIX QUOTA/DIREZIONE (7 Ott 2026): il plugin nativo espone anche quota e
    // direzione (formatLocation in BackgroundGeolocation.java). Senza
    // propagarle, i waypoint registrati avevano `elevation_m`/`heading` a null
    // e le statistiche (dislivello, pendenza, curve) restavano a 0.
    altitude: typeof loc.altitude === "number" ? loc.altitude : null,
    heading: typeof loc.bearing === "number" ? loc.bearing : null,
    timestamp: loc.time ?? Date.now(),
    totalDistanceKm: _totalDistanceKm,
    sessionId: _sessionId,
  };
}

function notifyPositionListeners(pos: GpsPosition): void {
  for (const fn of _positionListeners) {
    try {
      fn(pos);
    } catch (e) {
      /* ignora */
    }
  }
}

function notifyFlushListeners(data: PendingFlushData): void {
  for (const fn of _flushListeners) {
    try {
      fn(data);
    } catch (e) {
      /* ignora */
    }
  }
}

// ── FIX GPS-ADAPTIVE (Set 2026): helpers watcher adattivo ───────────────────

function buildWatcherSettings(intervalMs: number): any {
  const settings: any = {
    backgroundMessage: "RideConnect sta registrando il tuo percorso",
    backgroundTitle: "🏍️ Registrazione in corso",
    requestPermissions: true,
    stale: false,
    // FIX CURVA-LIVE (02 Set 2026): filtro 3m + emissione ogni 3s anche da
    // fermi: la curva del percorso appare subito e segue in tempo reale.
    distanceFilter: 3,
    interval: intervalMs,
  };
  if (_trackingConfig.fastestInterval) {
    settings.fastestInterval = Math.round(
      _trackingConfig.fastestInterval * 1000,
    );
  }
  return settings;
}

function watcherCallback(location: any, error: any): void {
  if (error) {
    console.error("[GeoBridge] Location error:", error?.message || error);
    if ((error as any)?.code === 1) {
      _gpsAvailable = false;
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("rideconnect:gps-unavailable", {
            detail: {
              reason: "permission-denied",
              message: (error as any)?.message || "",
            },
          }),
        );
      }
    }
    return;
  }
  if (location) {
    _gpsAvailable = true;
    const pos = toGpsPosition(location);
    _lastPosition = pos;
    _pendingLocations++;
    heartbeatRecordFix(); // FASE 4: segnala che il plugin è vivo
    // FIX GPS-ADAPTIVE: alimenta il controller con la velocità (km/h)
    _adaptiveController?.onFix(pos.speed);
    notifyPositionListeners(pos);
  }
}

/**
 * Riconfigura il watcher con il nuovo intervallo. GUARDED: se qualcosa
 * fallisce, riavvia il watcher con l'ultimo intervallo buono — il tracking
 * non si interrompe mai.
 */
async function restartWatcherWithInterval(intervalMs: number): Promise<void> {
  if (!_isTracking || !_watcherId) return;
  // FIX (7 Ott 2026): il plugin community NON supporta l'opzione `interval`
  // (accetta solo distanceFilter: l'intervallo nativo è fisso a 1s, vedi
  // BackgroundGeolocationService.addWatcher), quindi riavviare il watcher non
  // cambia il campionamento. In background è anche pericoloso: se la WebView
  // viene sospesa fra removeWatcher e addWatcher il watcher resta morto →
  // buco nel tracciato. A schermo spento si salta il riavvio.
  if (
    typeof document !== "undefined" &&
    document.visibilityState === "hidden"
  ) {
    console.log("[GeoBridge] Adaptive restart skipped (app in background)");
    return;
  }
  const previousId = _watcherId;
  const previousInterval = _currentIntervalMs;
  try {
    await BackgroundGeolocation.removeWatcher({ id: previousId });
    _watcherId = await BackgroundGeolocation.addWatcher(
      buildWatcherSettings(intervalMs),
      watcherCallback,
    );
    _currentIntervalMs = intervalMs;
    console.log(`[GeoBridge] ✅ Adaptive interval → ${intervalMs}ms`);
  } catch (err: any) {
    console.error(
      "[GeoBridge] Adaptive reconfiguration failed:",
      err?.message || err,
    );
    try {
      _watcherId = await BackgroundGeolocation.addWatcher(
        buildWatcherSettings(previousInterval),
        watcherCallback,
      );
    } catch (err2: any) {
      console.error(
        "[GeoBridge] CRITICO: watcher non riavviabile:",
        err2?.message || err2,
      );
      _isTracking = false;
      _watcherId = null;
    }
  }
}

// ── Public API ──────────────────────────────────────────────────────────────

export async function start(
  callback?: (pos: GpsPosition) => void,
): Promise<boolean> {
  if (_isTracking) {
    console.log("[GeoBridge] Already tracking");
    if (callback) _positionListeners.push(callback);
    return true;
  }

  if (!Capacitor.isNativePlatform()) {
    console.log(
      "[GeoBridge] Non-native platform — background tracking disabled",
    );
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("rideconnect:gps-unavailable", {
          detail: {
            reason: "non-native-platform",
            message: "GPS background non disponibile su web.",
          },
        }),
      );
    }
    return false;
  }

  try {
    if (callback) _positionListeners.push(callback);

    _watcherId = await BackgroundGeolocation.addWatcher(
      buildWatcherSettings(_currentIntervalMs),
      watcherCallback,
    );

    _isTracking = true;
    // FIX GPS-ADAPTIVE (Set 2026): controller adattivo con isteresi —
    // riconfigura il watcher solo su cambio banda stabile (debounce 15s,
    // max 1 riconfigurazione/60s).
    _adaptiveController = new AdaptiveIntervalController({
      initialIntervalMs: _currentIntervalMs,
      apply: (ms) => {
        void restartWatcherWithInterval(ms);
      },
    });
    // FASE 4: Avvia heartbeat monitor per rilevare plugin death
    startHeartbeatMonitor((alive, failures) => {
      if (!alive && _isTracking) {
        console.error(
          "[GeoBridge] Heartbeat detected plugin death — triggering recovery",
        );
        // Il consumer (useRouteRecording) reagirà all'evento rideconnect:gps-plugin-dead
      }
    });
    console.log(
      "[GeoBridge] ✅ Background tracking started (community plugin) — watcherId:",
      _watcherId,
    );
    return true;
  } catch (err: any) {
    console.error("[GeoBridge] Failed to start tracking:", err?.message || err);
    _isTracking = false;
    _watcherId = null;
    return false;
  }
}

export async function stop(): Promise<void> {
  if (!_isTracking || !_watcherId) return;

  try {
    await BackgroundGeolocation.removeWatcher({ id: _watcherId });
    _isTracking = false;
    _watcherId = null;
    _positionListeners = [];
    // FIX GPS-ADAPTIVE: ferma il controller adattivo
    _adaptiveController?.dispose();
    _adaptiveController = null;
    _currentIntervalMs = 3000;
    stopHeartbeatMonitor(); // FASE 4: ferma heartbeat monitor
    console.log("[GeoBridge] ✅ Tracking stopped (community plugin)");
  } catch (err: any) {
    console.warn("[GeoBridge] Stop tracking error:", err?.message || err);
    _isTracking = false;
    _watcherId = null;
    stopHeartbeatMonitor(); // FASE 4: ferma heartbeat anche in caso di errore
  }
}

export function isTracking(): boolean {
  return _isTracking;
}

export async function getTrackingStatus(): Promise<TrackingStatus> {
  return {
    tracking: _isTracking,
    pendingLocations: _pendingLocations,
    totalDistanceKm: _totalDistanceKm,
    sessionId: _sessionId,
  };
}

export async function configure(config: TrackingConfig): Promise<void> {
  // FIX GPS-ADAPTIVE (Set 2026): il config viene SALVATO e usato da
  // buildWatcherSettings (es. fastestInterval); le bande adattive sono
  // condivise in adaptiveGps.ts. Prima questo metodo faceva solo log.
  _trackingConfig = { ..._trackingConfig, ...config };
  console.log("[GeoBridge] Configure:", _trackingConfig);
  if (config.distanceFilter && _isTracking) {
    console.log("[GeoBridge] distanceFilter change requires watcher restart");
  }
}

export async function getPendingStats(): Promise<PendingStats> {
  return {
    totalPending: _pendingLocations,
    sessionPending: _pendingLocations,
    sessionId: _sessionId,
  };
}

export async function syncPendingLocations(): Promise<{ triggered: boolean }> {
  // FIX (Ago 2026): su web il background tracking e' disabilitato (start() e' no-op),
  // quindi sync non deve mai riportare triggered=true — graceful degradation.
  if (!Capacitor.isNativePlatform()) return { triggered: false };
  if (_pendingLocations > 0 && _lastPosition) {
    notifyFlushListeners({
      locations: JSON.stringify([_lastPosition]),
      localIds: "",
      count: _pendingLocations,
      batchId: Date.now(),
      sessionId: _sessionId,
    });
  }
  return { triggered: true };
}

export async function ackPendingFlush(
  ids: number[],
): Promise<{ acked: number }> {
  // FIX (Ago 2026): su web non c'e' coda pending → acked deve essere 0.
  if (!Capacitor.isNativePlatform()) return { acked: 0 };
  _pendingLocations = Math.max(0, _pendingLocations - ids.length);
  return { acked: ids.length };
}

export async function requestBatteryOptimizationExemption(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  console.log(
    "[GeoBridge] Battery exemption — Foreground Service is auto-exempt on Android 12+",
  );
}

export function isAvailable(): boolean {
  return Capacitor.isNativePlatform();
}

export function isGpsAvailable(): boolean {
  return _gpsAvailable;
}

export function setGpsAvailable(available: boolean): void {
  _gpsAvailable = available;
}

export function addDistanceKm(km: number): void {
  _totalDistanceKm += km;
}

export function onFlush(
  callback: (data: PendingFlushData) => void,
): () => void {
  _flushListeners.push(callback);
  return () => {
    _flushListeners = _flushListeners.filter((fn) => fn !== callback);
  };
}

// ── Compatibilità con vecchia API ForegroundGeo ─────────────────────────────

export const ForegroundGeo = {
  start: (callback?: (pos: GpsPosition) => void) => start(callback),
  stop: () => stop(),
  configure: (opts: TrackingConfig) => configure(opts),
  isTracking: async () => getTrackingStatus(),
  requestBatteryOptimizationExemption: () =>
    requestBatteryOptimizationExemption(),
  getPendingStats: () => getPendingStats(),
  syncPendingLocations: () => syncPendingLocations(),
  ackPendingFlush: (ids: number[]) => ackPendingFlush(ids),
  addListener: (event: string, callback: (data: any) => void) => {
    if (event === "position") {
      _positionListeners.push(callback);
      return {
        remove: () => {
          _positionListeners = _positionListeners.filter(
            (fn) => fn !== callback,
          );
        },
      };
    }
    if (event === "pendingFlush") {
      return { remove: onFlush(callback) };
    }
    return { remove: () => {} };
  },
  removeAllListeners: async () => {
    _positionListeners = [];
    _flushListeners = [];
  },
};

export const BackgroundGeo = ForegroundGeo;
